import { stripLinksForReading } from '@/lib/links';

/**
 * The email body shaped for the Review tab's "View Source Reference".
 *
 * `body` is what the section shows; `footer` is the trailing newsletter
 * boilerplate (unsubscribe / "you are receiving this email" block), kept
 * separately so the UI can fold it behind a toggle. Nothing is deleted
 * except links (see `stripLinksForReading`) and orphaned button labels.
 */
export interface SourceForReading {
  body: string;
  footer: string | null;
}

/**
 * Newsletter buttons that the HTML cleaner leaves on a line of their own
 * once the link is stripped. A line is dropped only when it IS one of these
 * (case-insensitive, trimmed, optional trailing arrow / ellipsis) — the same
 * words inside a sentence are untouched.
 */
const BUTTON_LABELS = new Set(
  [
    'Register',
    'Register now',
    'Register here',
    'Learn more',
    'Read more',
    'Click here',
    'View in browser',
    'View online',
    'View this email in your browser',
    'Not displaying correctly? View in browser',
    'View all flyers',
  ].map((label) => label.toLowerCase()),
);

/** Trailing decoration a button label may carry: `Read more »`, `Learn more →`. */
const BUTTON_DECORATION = /[\s.…»›→>]+$/u;

/** A line that opens the standard newsletter footer. */
const FOOTER_MARKERS: readonly RegExp[] = [
  /^unsubscribe\b/i,
  /^you(?: are|'re|’re) receiving this (?:e-?mail|message)\b/i,
  /^email preferences\b/i,
  /^this (?:e-?mail|message) (?:has been|was) sent to you by\b/i,
];

/** The footer is only split off when it starts in the last quarter of the text. */
const FOOTER_WINDOW = 0.75;

/** A line that starts a list item — its line break is kept when joining. */
const LIST_ITEM = /^(?:[•·▪◦*-]\s|\d{1,2}[.)]\s)/u;

function isButtonLabel(line: string): boolean {
  const key = line.replace(BUTTON_DECORATION, '').trim().toLowerCase();
  return key !== '' && BUTTON_LABELS.has(key);
}

function isFooterMarker(line: string): boolean {
  return FOOTER_MARKERS.some((re) => re.test(line));
}

/** Drop leading/trailing blank lines and squeeze blank runs to one. */
function collapseBlankLines(lines: string[]): string[] {
  const out: string[] = [];
  for (const line of lines) {
    if (line === '' && (out.length === 0 || out[out.length - 1] === '')) {
      continue;
    }
    out.push(line);
  }
  while (out.length > 0 && out[out.length - 1] === '') out.pop();
  return out;
}

/**
 * Join hard-wrapped lines into paragraphs: a single newline becomes a space,
 * a blank line stays a paragraph break. Plain-text mail is wrapped at ~72
 * columns, so without this a paragraph renders as a ragged column. A line
 * that starts a list item keeps its break so bullets don't run together.
 */
function joinParagraphLines(lines: string[]): string {
  let out = '';
  lines.forEach((line, i) => {
    if (i === 0) {
      out = line;
      return;
    }
    const prev = lines[i - 1];
    const keepBreak = line === '' || prev === '' || LIST_ITEM.test(line);
    out += keepBreak ? `\n${line}` : ` ${line}`;
  });
  return out;
}

/**
 * Turn `emails.processed_content` into readable prose.
 *
 *   1. CRLF / CR → LF (old rows were stored before the server normalized it).
 *   2. Strip links.
 *   3. Trim every line.
 *   4. A bullet the cleaner left alone on its line (`•` then the text on the
 *      next non-blank line) is joined back to its text.
 *   5. Lines that are only a button label are dropped.
 *   6. Any run of blank lines becomes one blank line.
 *   7. The LAST footer-marker line, if it starts in the final quarter of the
 *      text, begins the footer — returned separately, never discarded.
 *   8. Hard-wrapped lines are joined into paragraphs.
 *
 * Pure and language-agnostic: no heuristics beyond the fixed phrase lists,
 * so bilingual bodies pass through intact.
 */
export function formatSourceForReading(text: string): SourceForReading {
  const stripped = stripLinksForReading(text.replace(/\r\n?/g, '\n'));
  const trimmed = stripped.split('\n').map((line) => line.trim());

  const lines: string[] = [];
  for (let i = 0; i < trimmed.length; i += 1) {
    const line = trimmed[i];
    if (line === '•') {
      let next = i + 1;
      while (next < trimmed.length && trimmed[next] === '') next += 1;
      if (next < trimmed.length) {
        lines.push(`• ${trimmed[next]}`);
        i = next;
        continue;
      }
    }
    if (isButtonLabel(line)) continue;
    lines.push(line);
  }

  const collapsed = collapseBlankLines(lines);

  const totalLength = collapsed.join('\n').length;
  let footerStart = -1;
  let offset = 0;
  collapsed.forEach((line, i) => {
    if (
      i > 0 &&
      isFooterMarker(line) &&
      offset >= totalLength * FOOTER_WINDOW
    ) {
      footerStart = i;
    }
    offset += line.length + 1;
  });

  if (footerStart === -1) {
    return { body: joinParagraphLines(collapsed), footer: null };
  }

  const bodyLines = collapseBlankLines(collapsed.slice(0, footerStart));
  const footerLines = collapsed.slice(footerStart);
  return {
    body: joinParagraphLines(bodyLines),
    footer: joinParagraphLines(footerLines),
  };
}
